import type { Invitation } from "@event-planner/shared";

/**
 * Organizer-facing rollup of an invitation's email fields.
 *
 * The server writes several independent facts (`emailSentAt`,
 * `emailDeliveryStatus`, `emailDeliveryError`, `emailSendCount`,
 * `emailMarkedSentManuallyAt`). This collapses them into one state the
 * roster chip and the send dialog can reason about.
 */
export type InvitationEmailState =
  /** Nothing has gone out and nobody has marked it sent. */
  | "not_sent"
  /** Queue doc written; waiting on the mail transport. */
  | "sending"
  /**
   * The transport accepted the message (Microsoft Graph 202, or SMTP 250 on
   * the extension path). Not proof of inbox placement; a bounce arriving
   * later flips the row to `failed`.
   */
  | "delivered"
  /** The transport rejected the message, or a bounce came back — see `error`. */
  | "failed"
  /** SUPPRESS_EMAILS was on at send time; nothing left the server. */
  | "suppressed"
  /** Legacy rows: a system send is recorded but no delivery status. */
  | "sent"
  /** Organizer recorded an out-of-band send. */
  | "sent_manually";

export interface InvitationEmailSummary {
  state: InvitationEmailState;
  /** Most recent system send, if any. */
  lastSentAt: Date | null;
  /** When an organizer marked the email sent manually, if ever. */
  markedManuallyAt: Date | null;
  sendCount: number;
  error: string | null;
}

/** States that mean "this person still needs an email." */
export const NEEDS_EMAIL_STATES: ReadonlySet<InvitationEmailState> = new Set([
  "not_sent",
  "suppressed",
  "failed",
]);

function toDate(value: Invitation["emailSentAt"]): Date | null {
  return value?.toDate?.() ?? null;
}

export function deriveInvitationEmailState(inv: Invitation): InvitationEmailSummary {
  const lastSentAt = toDate(inv.emailSentAt);
  const markedManuallyAt = toDate(inv.emailMarkedSentManuallyAt);
  const status = inv.emailDeliveryStatus ?? null;

  let state: InvitationEmailState;
  if (status === "queued") state = "sending";
  else if (status === "failed") state = "failed";
  else if (status === "sent") state = "delivered";
  else if (status === "suppressed") state = "suppressed";
  else if (lastSentAt) state = "sent";
  else state = "not_sent";

  // A manual mark newer than the last system attempt overrides a
  // non-successful system state: the organizer is telling us the person
  // got the email some other way.
  const manualIsNewer =
    !!markedManuallyAt && (!lastSentAt || markedManuallyAt > lastSentAt);
  if (manualIsNewer && NEEDS_EMAIL_STATES.has(state)) {
    state = "sent_manually";
  } else if (state === "not_sent" && markedManuallyAt) {
    state = "sent_manually";
  }

  return {
    state,
    lastSentAt,
    markedManuallyAt,
    sendCount: inv.emailSendCount ?? 0,
    error: inv.emailDeliveryError ?? null,
  };
}

export const EMAIL_STATE_LABEL: Record<InvitationEmailState, string> = {
  not_sent: "Not sent",
  sending: "Sending",
  delivered: "Sent",
  failed: "Failed",
  suppressed: "Suppressed",
  sent: "Sent",
  sent_manually: "Sent manually",
};

export const EMAIL_STATE_COLOR: Record<
  InvitationEmailState,
  "default" | "success" | "warning" | "error" | "info"
> = {
  not_sent: "warning",
  sending: "info",
  delivered: "success",
  failed: "error",
  suppressed: "default",
  sent: "success",
  sent_manually: "success",
};
